import * as actionTypes from "./actionTypes";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { getLocalizedString, getErrorStringFromServerError } from "@icarius-localization/strings";
import {
  getQueryDefinitionAPI,
  createQueryDefinitionAPI,
  modifyQueryDefinitionAPI,
  deleteQueryDefinitionAPI,
  duplicateQueryDefinitionAPI,
  exportQueryDefinitionAPI,
  importQueryDefinitionAPI,
  generateQueryDefinitionAPI,
  implementQueryDefinitionAPI,
  testQueryDefinitionAPI,
} from "@icarius-connection/api";
import { downloadBlob } from "@icarius-utils/download";
import { customFileTypes } from "@icarius-utils/constants";

const errorHandler = (e, dispatch) => {
  let errorString = "";
  switch (e.response?.data?.status) {
    case "VALIDATION_ERROR":
      // Problemas de validación de campos: el backend detalla el campo en errors.message
      errorString = e.response?.data?.errors?.message || getLocalizedString("validationError");
      break;
    case "INVALID_DATA":
      // Problema con la consulta (no con los campos): el backend describe el detalle en "error"
      errorString = e.response?.data?.error || getErrorStringFromServerError(e);
      break;
    case "DUPLICATED_DATA":
      errorString = getLocalizedString("cannotHaveMoreThanOneQuerySharingSameCodeOrName");
      break;
    case "IN_USE":
      errorString = getLocalizedString("queryCannotBeDeletedBecauseItIsAssociatedWithSpecialProcess");
      break;
    default:
      errorString = getErrorStringFromServerError(e);
      break;
  }
  dispatch(openSnackbarAction({ msg: errorString, severity: "error" }));
}

const formatIsAiGenerated = (rows) =>
  (rows || []).map((row) => ({
    ...row,
    isAiGeneratedString: getLocalizedString(row.isAiGenerated ? "yes" : "no"),
  }));

export const getQueryDefinitionAction = () => async (dispatch) => {
  dispatch({ type: actionTypes.GET_ROWS });
  try {
    let response = await getQueryDefinitionAPI();

    let rows = formatIsAiGenerated(response.data && response.data.data);
    let dataTypes = response.data && response.data.dataTypes;
    let roleTypes = response.data && response.data.rolTypes;
    let activeTypes = response.data && response.data.activeTypes;
    let dateFormat = response.data && response.data.dateFormat;

    dispatch({
      type: actionTypes.GET_ROWS_FULFILLED,
      payload: {
        rows,
        dataTypes,
        roleTypes,
        activeTypes,
        dateFormat,
      }
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GET_ROWS_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const createQueryDefinitionAction = (data) => async (dispatch) => {
  dispatch({ type: actionTypes.CREATE });
  try {
    let response = await createQueryDefinitionAPI(data);

    let rows = formatIsAiGenerated(response.data && response.data.data);
    let dataTypes = response.data && response.data.dataTypes;
    let roleTypes = response.data && response.data.rolTypes;
    let activeTypes = response.data && response.data.activeTypes;
    let dateFormat = response.data && response.data.dateFormat;

    dispatch({
      type: actionTypes.CREATE_FULFILLED,
      payload: {
        rows,
        dataTypes,
        roleTypes,
        activeTypes,
        dateFormat,
      }
    });
    dispatch(openSnackbarAction({ msg: getLocalizedString("queryDefinitionCreatedSuccessfully"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.CREATE_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const modifyQueryDefinitionAction = (data) => async (dispatch) => {
  dispatch({ type: actionTypes.MODIFY });
  try {
    let response = await modifyQueryDefinitionAPI(data);

    let rows = formatIsAiGenerated(response.data && response.data.data);
    let dataTypes = response.data && response.data.dataTypes;
    let roleTypes = response.data && response.data.rolTypes;
    let activeTypes = response.data && response.data.activeTypes;
    let dateFormat = response.data && response.data.dateFormat;

    dispatch({
      type: actionTypes.MODIFY_FULFILLED,
      payload: {
        rows,
        dataTypes,
        roleTypes,
        activeTypes,
        dateFormat,
      }
    });
    dispatch(openSnackbarAction({ msg: getLocalizedString("queryDefinitionModifiedSuccessfully"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.MODIFY_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const deleteQueryDefinitionAction = (data) => async (dispatch) => {
  dispatch({ type: actionTypes.DELETE });
  try {
    let response = await deleteQueryDefinitionAPI(data);

    let rows = formatIsAiGenerated(response.data && response.data.data);
    let dataTypes = response.data && response.data.dataTypes;
    let roleTypes = response.data && response.data.rolTypes;
    let activeTypes = response.data && response.data.activeTypes;
    let dateFormat = response.data && response.data.dateFormat;
    let status = response.data && response.data.status;

    if (status !== "OK") {
      dispatch({ type: actionTypes.DELETE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.DELETE_FULFILLED,
      payload: {
        rows,
        dataTypes,
        roleTypes,
        activeTypes,
        dateFormat,
      }
    });

    dispatch(openSnackbarAction({ msg: getLocalizedString("queryDefinitionDeletedSuccessfully"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DELETE_REJECTED });
    errorHandler(e, dispatch);
  }
};

export const duplicateQueryDefinitionAction = (data) => async (dispatch) => {
  dispatch({ type: actionTypes.DUPLICATE });
  try {
    let response = await duplicateQueryDefinitionAPI(data);

    let rows = formatIsAiGenerated(response.data && response.data.data);
    let dataTypes = response.data && response.data.dataTypes;
    let roleTypes = response.data && response.data.rolTypes;
    let activeTypes = response.data && response.data.activeTypes;
    let dateFormat = response.data && response.data.dateFormat;

    dispatch({
      type: actionTypes.DUPLICATE_FULFILLED,
      payload: {
        rows,
        dataTypes,
        roleTypes,
        activeTypes,
        dateFormat,
      }
    });
    dispatch(openSnackbarAction({ msg: getLocalizedString("duplicatedQueryDefinitionSuccessfully"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.DUPLICATE_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const exportQueryDefinitionAction = (data) => async (dispatch) => {
  dispatch({ type: actionTypes.EXPORT });
  try {
    let response = await exportQueryDefinitionAPI(data);

    dispatch({ type: actionTypes.EXPORT_FULFILLED });
    downloadBlob(response.data, customFileTypes.icaquery, data.code);
    dispatch(openSnackbarAction({ msg: getLocalizedString("queryDefinitionExportedSuccessfully"), severity: "success" }));
    return response;
  } catch (e) {
    // Al pedir responseType "blob", el error del backend también llega como Blob: lo parseo para leer el status
    if (e.response?.data instanceof Blob) {
      try {
        e.response.data = JSON.parse(await e.response.data.text());
      } catch { /* Si no se puede parsear, se maneja como error genérico */ }
    }
    dispatch({ type: actionTypes.EXPORT_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const importQueryDefinitionAction = (file) => async (dispatch) => {
  dispatch({ type: actionTypes.IMPORT });
  try {
    let formData = new FormData();
    formData.append("FILE", file);

    let response = await importQueryDefinitionAPI(formData);

    let rows = formatIsAiGenerated(response.data && response.data.data);
    let dataTypes = response.data && response.data.dataTypes;
    let roleTypes = response.data && response.data.rolTypes;
    let activeTypes = response.data && response.data.activeTypes;
    let dateFormat = response.data && response.data.dateFormat;

    dispatch({
      type: actionTypes.IMPORT_FULFILLED,
      payload: {
        rows,
        dataTypes,
        roleTypes,
        activeTypes,
        dateFormat,
      }
    });
    dispatch(openSnackbarAction({ msg: getLocalizedString("queryDefinitionImportedSuccessfully"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.IMPORT_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const generateQueryDefinitionAction = (data) => async (dispatch) => {
  dispatch({ type: actionTypes.GENERATE });
  try {
    let response = await generateQueryDefinitionAPI(data);

    let status = response.data && response.data.status;
    let listTypes = response.data && response.data.data.listTypes;
    let paramTypes = response.data && response.data.data.paramTypes;
    let requiredTypes = response.data && response.data.data.requiredTypes;

    if (status !== "OK") {
      dispatch({ type: actionTypes.GENERATE_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("defaultException"), severity: "error" }));
      return;
    }

    dispatch({
      type: actionTypes.GENERATE_FULFILLED,
      payload: {
        listTypes,
        paramTypes,
        requiredTypes,
      }
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.GENERATE_REJECTED, payload: e });
    errorHandler(e, dispatch);
    // Se devuelve el error para que el hook pueda detectar un generationId
    // inválido (422 INVALID_DATA) y reiniciar la generación.
    return e;
  }
};

export const implementQueryDefinitionAction = (data) => async (dispatch) => {
  dispatch({ type: actionTypes.IMPLEMENT });
  try {
    let response = await implementQueryDefinitionAPI(data);

    let rows = response.data && response.data.data;
    let dataTypes = response.data && response.data.dataTypes;
    let roleTypes = response.data && response.data.rolTypes;
    let activeTypes = response.data && response.data.activeTypes;
    let dateFormat = response.data && response.data.dateFormat;

    dispatch({
      type: actionTypes.IMPLEMENT_FULFILLED,
      payload: {
        rows,
        dataTypes,
        roleTypes,
        activeTypes,
        dateFormat,
      }
    });
    dispatch(openSnackbarAction({ msg: getLocalizedString("queryDefinitionImplementedSuccessfully"), severity: "success" }));
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.IMPLEMENT_REJECTED, payload: e });
    errorHandler(e, dispatch);
  }
};

export const testQueryDefinitionAction = (data) => async (dispatch) => {
  dispatch({ type: actionTypes.TEST_QUERY });
  try {
    let response = await testQueryDefinitionAPI(data);

    let status = response.data && response.data.status;
    let filters = response.data && response.data.filters;
    let result = response.data && response.data.result;
    let context = response.data && response.data.context;
    let dateFormat = response.data && response.data.date_format;
    let currencyLocalization = response.data && response.data.currency_localization;
    let error = response.data && response.data.error;

    if (status !== "OK") {
      dispatch({ type: actionTypes.TEST_QUERY_REJECTED });
      dispatch(openSnackbarAction({ msg: getLocalizedString("errorExecutingQuery"), severity: "error" }));
      return response;
    }

    if (error) {
      dispatch(openSnackbarAction({ msg: error, severity: "error", duration: null }));
    }

    dispatch({
      type: actionTypes.TEST_QUERY_FULFILLED,
      payload: {
        filters,
        result,
        context,
        dateFormat,
        currencyLocalization,
      }
    });
    return response;
  } catch (e) {
    dispatch({ type: actionTypes.TEST_QUERY_REJECTED, payload: e });
    errorHandler(e, dispatch);
    // Se devuelve el error para que el hook pueda saber que la prueba no se ejecutó.
    return e;
  }
};

